import React, { useContext } from 'react'
import styles from './styles.module.scss'
import { ticks } from 'd3-array'
import { percentFormatter } from 'lib/formatters'
import { animated, useSpring, config as springConfig } from 'react-spring'
import { colorGenerator } from 'lib/colours'
import { LocaleContext } from 'contexts/locale'
import arrowDown from 'images/arrow-down.svg'
import arrowUp from 'images/arrow-up.svg'

/**
 * The standardised width of the scale
 * @type {number}
 */
const SCALE_WIDTH = 100
const MIN_DELTA = 7.5

export interface ScaleProps {
  overallMinimum: number
  overallMaximum: number
  minimum: number
  maximum: number
}

/**
 * Shows a value scale, using the overall range as well as the currently
 * shown range.
 */
export function Scale({
  overallMinimum,
  overallMaximum,
  minimum,
  maximum
}: ScaleProps) {
  const locale = useContext(LocaleContext)
  const messages = require('./locales/' + locale + '.yml')
  const normalize = (val: number) =>
    (val - overallMinimum) / (overallMaximum - overallMinimum)

  const Caption = ({
    value,
    formatter = percentFormatter(locale)
  }: {
    value: number
    formatter?: (input: number) => string
  }) => (
    <div
      style={{
        left: `${normalize(value) * 100}%`
      }}
      className={styles.number}
    >
      {formatter(value * 0.01)}
    </div>
  )

  let normalizedMinimum: number = normalize(minimum) * SCALE_WIDTH
  let normalizedMaximum: number = normalize(maximum) * SCALE_WIDTH
  let normalizedDelta = normalizedMaximum - normalizedMinimum

  const maxWindowWidth = 1200
  const windowWidthFactor =
    window.innerWidth < maxWindowWidth ? window.innerWidth : maxWindowWidth
  let tickCount = windowWidthFactor * 0.01
  const screenUnit = maxWindowWidth / windowWidthFactor
  let scaleTicks: number[] = ticks(overallMinimum, overallMaximum, tickCount)

  // classic iteration, since IE11 doesn't understand `Array.prototype.keys()`.
  const gradientStops = []
  for (let i = 0; i < 10; i++) {
    gradientStops.push(
      <stop
        key={`color-${i}`}
        offset={`${(i / 10) * 100}%`}
        stopColor={colorGenerator(i / 10)}
      />
    )
  }

  return (
    <div className='scale'>
      <div className={styles.legendTop}>
        {messages['legendSelection']}{' '}
        <img
          src={arrowDown}
          alt={messages['arrowDown']}
          className={styles.arrowTop}
        />
      </div>
      <div className={styles.caption}>
        {scaleTicks.map((tick, index) => {
          return (
            <Caption
              key={tick}
              value={tick}
              formatter={percentFormatter(locale, 0)}
            />
          )
        })}
      </div>
      <svg
        viewBox={`0 0 ${SCALE_WIDTH} 1`}
        preserveAspectRatio='none'
        className={`strip ${styles.svgContainer}`}
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id='scale-gradient'>{gradientStops}</linearGradient>
        </defs>
        <rect
          x='0'
          y='0'
          width='100%'
          height='100%'
          fill='#CDD2D3'
          className='background'
        />
        {!isNaN(normalizedMinimum) && !isNaN(normalizedDelta) && (
          <g className='overall ticks'>
            {scaleTicks.map((tick, index) => {
              return (
                <line
                  key={tick}
                  x1={normalize(tick) * SCALE_WIDTH}
                  y1={-0.5}
                  x2={normalize(tick) * SCALE_WIDTH}
                  y2={1}
                  fill='none'
                  stroke='#999'
                  strokeLinecap='round'
                  strokeWidth={screenUnit / 5}
                />
              )
            })}
          </g>
        )}{' '}
        {!isNaN(normalizedMinimum) && !isNaN(normalizedDelta) && (
          <rect
            x={normalizedMinimum}
            y={0}
            width={normalizedDelta}
            height='100%'
            fill='url(#scale-gradient)'
          />
        )}
        {!isNaN(normalizedMinimum) && !isNaN(normalizedDelta) && (
          <g>
            {normalizedDelta > MIN_DELTA && (
              <line
                x1={normalize(minimum) * SCALE_WIDTH}
                y1={0}
                x2={normalize(minimum) * SCALE_WIDTH}
                y2={1.5}
                fill='none'
                stroke='#999'
                strokeLinecap='round'
                strokeWidth={screenUnit / 5}
              />
            )}
            <line
              x1={normalize(maximum) * SCALE_WIDTH}
              y1={0}
              x2={normalize(maximum) * SCALE_WIDTH}
              y2={1.5}
              fill='none'
              stroke='#999'
              strokeLinecap='round'
              strokeWidth={screenUnit / 5}
            />
          </g>
        )}
      </svg>

      <div className={styles.captionBottom} style={{ position: 'relative' }}>
        {normalizedDelta > MIN_DELTA && <Caption value={minimum} />}
        <Caption value={maximum} />
      </div>
    </div>
  )
}

/**
 * The same as {@link scale}, but with react-motion applied.
 * @see {@link scale}
 */
export function MotionScale(props: ScaleProps) {
  const AnimatedScale = animated(Scale)

  const animatedProps = useSpring({
    to: {
      minimum: props.minimum,
      overallMinimum: props.overallMinimum,
      maximum: props.maximum,
      overallMaximum: props.overallMaximum
    },
    from: { minimum: 0, overallMinimum: 0, maximum: 30, overallMaximum: 30 },
    config: springConfig.slow
  })
  return <AnimatedScale {...animatedProps} />
}
